import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Gift, ArrowLeft, RefreshCw, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function LoyaltySettingsPage() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/loyalty/settings`, authHeader());
      setCfg(r.data);
    } catch {
      toast.error('Einstellungen konnten nicht geladen werden');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/loyalty/settings`, {
        enabled: !!cfg.enabled,
        percent: Number(cfg.percent),
        min_session: Number(cfg.min_session),
        exclude_individual_price_customers: !!cfg.exclude_individual_price_customers,
      }, authHeader());
      setCfg(r.data);
      toast.success('Treue-Rabatt gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-8 text-muted-foreground">Lädt…</div>;

  const ord = cfg.min_session === 2 ? '2.' : `${cfg.min_session}.`;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="loyalty-settings-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="loyalty-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Gift className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">Treue-Rabatt</h1>
            <p className="text-sm text-muted-foreground">Belohne Kunden, die mehrmals pro Woche trainieren</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Gift className="w-5 h-5" /> „Mehrfach-Training pro Woche"</CardTitle>
          <CardDescription>
            Ab dem {ord} Training einer Kalenderwoche (Mo–So) erhält der Kunde {cfg.percent}% Rabatt –
            auf den Preis UND auf die Punkte. Es gewinnt der günstigere Preis (kein Stapeln mit Sonderpreisen).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Switch id="loyalty-enabled" data-testid="loyalty-enabled-switch"
              checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
            <Label htmlFor="loyalty-enabled">Treue-Rabatt aktiv</Label>
          </div>

          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Switch id="loyalty-exclude-individual" data-testid="loyalty-exclude-individual-switch"
              checked={!!cfg.exclude_individual_price_customers}
              onCheckedChange={(v) => setCfg({ ...cfg, exclude_individual_price_customers: v })} />
            <div className="space-y-0.5">
              <Label htmlFor="loyalty-exclude-individual">Kunden mit individuellen Preisen ausschließen</Label>
              <p className="text-xs text-muted-foreground">
                Hat ein Kunde für <strong>irgendeine</strong> Trainingsart einen individuellen Preis,
                bekommt er gar keinen Treue-Rabatt – auch nicht bei Trainingsarten ohne eigenen Sonderpreis.
                Ausgeschaltet: Der Rabatt greift bei Trainingsarten ohne individuellen Preis trotzdem.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="loyalty-percent">Rabatt (%)</Label>
              <Input id="loyalty-percent" data-testid="loyalty-percent-input" type="number" min="0" max="100"
                value={cfg.percent} onChange={(e) => setCfg({ ...cfg, percent: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="loyalty-min">Ab dem wievielten Training der Woche?</Label>
              <Input id="loyalty-min" data-testid="loyalty-min-input" type="number" min="2" max="10"
                value={cfg.min_session} onChange={(e) => setCfg({ ...cfg, min_session: e.target.value })} />
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Beispiel: 1. Training pro Woche zum normalen Preis, ab dem {ord} Training automatisch {cfg.percent}% günstiger.
              Kunden mit erst einem Training pro Woche werden per Push (max. 1×/Monat) an das günstigere nächste Training erinnert.
            </span>
          </div>

          <div className="flex justify-end">
            <Button data-testid="loyalty-save-btn" onClick={save} disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <CampaignCard />
    </div>
  );
}

function CampaignCard() {
  const [s, setS] = useState(null);
  const [recips, setRecips] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/second-training-campaign/settings`, authHeader()),
        axios.get(`${API}/second-training-campaign/recipients`, authHeader()),
      ]);
      setS(a.data);
      setRecips(b.data.count);
    } catch { toast.error('Hinweis-Einstellungen konnten nicht geladen werden'); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (u) => {
    try {
      const r = await axios.put(`${API}/second-training-campaign/settings`, u, authHeader());
      setS((p) => ({ ...p, ...r.data }));
      toast.success('Gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Hinweis jetzt an ALLE ${recips ?? '…'} Kunden ohne individuelle Preise senden?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/second-training-campaign/send-now`, {}, authHeader());
      toast.success(`Gesendet an ${data.sent} · E-Mail ${data.channels?.email || 0}, WhatsApp ${data.channels?.whatsapp || 0}, Push ${data.channels?.push || 0}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const createWa = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/second-training-campaign/whatsapp-template/create`, {}, authHeader());
      if (data.success) toast.success(`WhatsApp-Vorlage eingereicht (${data.status || 'PENDING'})`);
      else toast.error(`Fehler: ${JSON.stringify(data.error).slice(0, 160)}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Einreichen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const waStatus = s.whatsapp_template_status || 'NOT_CREATED';

  return (
    <Card data-testid="stc-campaign-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-cyan-600" /> Hinweis bewerben: „{s.percent}% aufs 2. Training"</CardTitle>
        <CardDescription>
          Erinnere Kunden an ihren Vorteil – als freundlicher „Wusstest du schon …"-Hinweis. Jede Einstellung einzeln schaltbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Automatischer Neukunden-Hinweis */}
        <div className="rounded-lg border p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Switch id="stc-onboarding" data-testid="stc-onboarding-switch"
              checked={!!s.onboarding_enabled} onCheckedChange={(v) => patch({ onboarding_enabled: v })} />
            <Label htmlFor="stc-onboarding" className="font-medium">Neukunden automatisch informieren</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Jeder neue Kunde (ohne individuelle Preise) erhält den Hinweis automatisch einige Tage nach der Registrierung.
          </p>
          <div className="w-60 space-y-1.5">
            <Label>Tage nach Registrierung</Label>
            <Input type="number" min="1" data-testid="stc-delay-input" defaultValue={s.onboarding_delay_days}
              onBlur={(e) => patch({ onboarding_delay_days: parseInt(e.target.value || '3', 10) })} />
          </div>
        </div>

        {/* Kanäle – jeder einzeln schaltbar */}
        <div className="space-y-2">
          <Label className="font-medium">Kanäle (jeder einzeln schaltbar)</Label>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="stc-email-switch" checked={!!s.send_email} onCheckedChange={(v) => patch({ send_email: v })} /> E-Mail</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="stc-whatsapp-switch" checked={!!s.send_whatsapp} onCheckedChange={(v) => patch({ send_whatsapp: v })} /> WhatsApp</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="stc-push-switch" checked={!!s.send_push} onCheckedChange={(v) => patch({ send_push: v })} /> Push</label>
          </div>
        </div>

        {/* WhatsApp-Vorlage */}
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="text-sm">
            WhatsApp-Vorlage: <span className={waStatus === 'APPROVED' ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>{waStatus}</span>
            {waStatus !== 'APPROVED' && <p className="text-xs text-muted-foreground">Bis zur Meta-Freigabe werden nur die anderen Kanäle versendet.</p>}
          </div>
          <Button variant="outline" size="sm" onClick={createWa} disabled={busy} data-testid="stc-create-wa-btn">
            {waStatus === 'NOT_CREATED' ? 'WhatsApp-Vorlage einreichen' : 'Erneut einreichen'}
          </Button>
        </div>

        {/* Rundruf an alle */}
        <div className="flex items-center justify-between rounded-lg border border-cyan-200 bg-cyan-50/40 p-3">
          <div className="text-sm">
            <p className="font-medium">An alle Kunden senden</p>
            <p className="text-xs text-muted-foreground">Einmaliger Rundruf an <strong>{recips ?? '…'}</strong> Kunden ohne individuelle Preise.</p>
          </div>
          <Button data-testid="stc-sendall-btn" onClick={sendAll} disabled={busy || !recips}
            className="bg-cyan-600 hover:bg-cyan-700 text-white">
            {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}Jetzt an alle senden
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
